// Puerto de packages/hce/src/hce/paciente/AlergiasTab.tsx. La constante
// CLINICAL_STATUS_ALERGIA_ACTIVA vive en el extractor de packages/hce
// (QuestionnaireEngine/extractors.ts) — se re-declara acá porque ese
// extractor entero (con el resto del motor de formularios) sigue sin
// migrar; es un literal FHIR estándar, no lógica de negocio propia.
import { useMedplum } from '@medplum/react-hooks';
import type { AllergyIntolerance } from '@medplum/fhirtypes';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { notify } from '@/components/ui/sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TablaVacia } from './TablaVacia';

const CLINICAL_STATUS_ALERGIA_ACTIVA = {
  coding: [{ system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical', code: 'active', display: 'Active' }],
};

function useAlergiasConRefresh(patientId: string) {
  const medplum = useMedplum();
  const [version, setVersion] = useState(0);
  const [alergias, setAlergias] = useState<AllergyIntolerance[] | undefined>(undefined);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let cancelado = false;
    setCargando(true);
    medplum
      // El search param FHIR de AllergyIntolerance.recordedDate es "date"
      // (no "recorded-date" como en Condition).
      .searchResources('AllergyIntolerance', { patient: `Patient/${patientId}`, _sort: '-date', _count: 100 })
      .then((r) => {
        if (!cancelado) {
          setAlergias(r as AllergyIntolerance[]);
          setCargando(false);
        }
      })
      .catch(() => {
        if (!cancelado) setCargando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, patientId, version]);

  return [alergias, cargando, () => setVersion((v) => v + 1)] as const;
}

export function AlergiasTab(): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [alergias, cargando, refrescar] = useAlergiasConRefresh(id);
  const [nueva, setNueva] = useState('');
  const [guardando, setGuardando] = useState(false);

  const agregar = async (): Promise<void> => {
    if (!nueva.trim()) return;
    setGuardando(true);
    try {
      await medplum.createResource<AllergyIntolerance>({
        resourceType: 'AllergyIntolerance',
        patient: { reference: `Patient/${id}` },
        clinicalStatus: CLINICAL_STATUS_ALERGIA_ACTIVA,
        code: { text: nueva.trim() },
        recordedDate: new Date().toISOString(),
      });
      setNueva('');
      refrescar();
    } catch (err) {
      notify({ color: 'red', message: err instanceof Error ? err.message : String(err), autoClose: false });
    }
    setGuardando(false);
  };

  const lista = (alergias ?? []) as AllergyIntolerance[];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-sm font-medium" htmlFor="nueva-alergia">
            {t('fichaPaciente.agregarAlergia')}
          </label>
          <Input id="nueva-alergia" value={nueva} onChange={(e) => setNueva(e.currentTarget.value)} placeholder={t('fichaPaciente.agregarAlergiaPlaceholder')} />
        </div>
        <Button onClick={() => void agregar()} disabled={guardando || !nueva.trim()}>
          {t('fichaPaciente.agregar')}
        </Button>
      </div>

      {cargando ? (
        <p className="text-sm text-muted-foreground">{t('fichaPaciente.cargando')}</p>
      ) : lista.length === 0 ? (
        <TablaVacia mensaje={t('fichaPaciente.sinAlergiasLarga')} />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('fichaPaciente.columnas.fecha')}</TableHead>
              <TableHead>{t('fichaPaciente.columnas.alergia')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lista.map((a) => (
              <TableRow key={a.id}>
                <TableCell>{a.recordedDate ? new Date(a.recordedDate).toLocaleDateString('es-EC') : '—'}</TableCell>
                <TableCell>{a.code?.text ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
